import { RolodexEntryJson, RolodexEntryType } from 'types'
import { parseGeoJsonGeometry, parseString } from 'utils/parsingHelpers'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { GeckoEntryBase } from './geckoEntry'

export function isGeckoOrganization(
  entry: GeckoEntryBase
): entry is GeckoOrganization {
  return entry instanceof GeckoOrganization
}

/**
 * A company, government organization, or other large institution. A root in
 * the hierarchy and a parent to other types like sites, units, and inspections.
 */
export class GeckoOrganization extends GeckoEntryBase<
  {
    name: string
    description: string
    geometry: GeoJSON.Geometry | undefined
  },
  {
    [RolodexEntryType.SALESFORCE_ACCOUNT]: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        name: parseString(json.data.name) ?? json.id,
        description: parseString(json.data.description) ?? '',
        geometry: parseGeoJsonGeometry(json.data.geometry),
      },
      {
        [RolodexEntryType.SALESFORCE_ACCOUNT]:
          parseString(json.tags[RolodexEntryType.SALESFORCE_ACCOUNT]) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates an organization from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoOrganization(json)
  }

  /**
   * A simple comparison method, used to sort entries by their names, properly
   * taking into account multi-digit numbers (i.e. 2 will appear before 20)
   * @param a     The first entry
   * @param b     The second entry
   */
  public static DefaultSort(a: GeckoOrganization, b: GeckoOrganization) {
    return compareAlphanumeric(a.data.name, b.data.name)
  }
}
